import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from './components/Navbar';
import LandingP from './LandingP';
import Buy from './FormBuy';
import ProductDetails from './ProductDetails';
import Footer from './Footer';
import WhatsAppButton from './WhatsAppButton';
import ScrollIndicator from './ScrollIndicator';
import Toast from './Toast';
import LegalPage from './LegalPage';
import StickyBuyBar from './components/StickyBuyBar';
import './CommonStyles.css';
import './App.css';

// Lazy load off-screen components
const BundleOffer = React.lazy(() => import('./BundleOffer'));
const HowToUse = React.lazy(() => import('./HowToUse'));
const Testimonials = React.lazy(() => import('./Testimonials'));
const FAQ = React.lazy(() => import('./FAQ'));

function App() {
    const { i18n } = useTranslation();
    const [page, setPage] = useState('home');
    const [activeProductKey, setActiveProductKey] = useState('blue');
    const [selectedBundle, setSelectedBundle] = useState(null);
    const [toast, setToast] = useState(null);
    const [policyModal, setPolicyModal] = useState(null);
    const [showSticky, setShowSticky] = useState(false);
    
    // Scroll to top on page change
    useEffect(() => {
        window.scrollTo(0, 0);
        setShowSticky(false);
    }, [page]);

    // Sticky Buy Bar Logic for Home Page
    useEffect(() => {
        if (page !== 'home') return;

        const handleScroll = () => {
            if (page === 'home') {
                // Show sticky if user scrolls more than 20px from top (~5mm)
                setShowSticky(window.scrollY > 20);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        const timer = setTimeout(handleScroll, 500);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(timer);
        };
    }, [page]);

    // Sync body class with sticky visibility
    useEffect(() => {
        if (showSticky) {
            document.body.classList.add('sticky-bar-visible');
        } else {
            document.body.classList.remove('sticky-bar-visible');
        }
        return () => document.body.classList.remove('sticky-bar-visible');
    }, [showSticky]);

    // Sync HTML attributes with current language
    useEffect(() => {
        const lng = i18n.language || 'ar';
        document.documentElement.lang = lng;
        document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
        document.body.dir = lng === 'ar' ? 'rtl' : 'ltr';
    }, [i18n.language]);

    // IntersectionObserver for Scroll Reveals
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                    observer.unobserve(entry.target);
                }
            });
        }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

        const scan = () => {
            document.querySelectorAll('.reveal-on-scroll:not(.is-visible)').forEach(el => observer.observe(el));
        };

        // Initial scan after a small delay
        const timeout = setTimeout(scan, 100);

        // Re-scan when DOM changes (for lazy components)
        const mutationObserver = new MutationObserver(scan);
        mutationObserver.observe(document.body, { childList: true, subtree: true });

        return () => {
            clearTimeout(timeout);
            observer.disconnect();
            mutationObserver.disconnect();
        };
    }, [page]);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const handlePageChange = (newPage) => {
        if (newPage === 'home' || newPage === 'details') {
            setSelectedBundle(null);
        }
        setPage(newPage);
    };

    const handleBuyClick = (bundle = null) => {
        setSelectedBundle(bundle);
        setPage('buy');
    };

    return (
        <div className="App">
            <Navbar setPage={handlePageChange} />

            {page === 'home' && (
                <>
                    <LandingP
                        setPage={handlePageChange}
                        activeKey={activeProductKey}
                        setActiveKey={setActiveProductKey}
                    />
                    <React.Suspense fallback={<div className="section-loading">Loading...</div>}>
                        <BundleOffer onSelect={handleBuyClick} />
                        <HowToUse activeKey={activeProductKey} />
                        <Testimonials />
                        <FAQ />
                    </React.Suspense>

                    <Footer setPolicyModal={setPolicyModal} setPage={setPage} />
                </>
            )}

            {page === 'buy' && (
                <>
                    <Buy
                        setPage={handlePageChange}
                        activeKey={activeProductKey}
                        showToast={showToast}
                        initialBundle={selectedBundle}
                    />
                    <Footer setPolicyModal={setPolicyModal} setPage={setPage} />
                </>
            )}

            {page === 'details' && (
                <>
                    <ProductDetails
                        setPage={handlePageChange}
                        activeKey={activeProductKey}
                        setActiveKey={setActiveProductKey}
                    />
                    <Footer setPolicyModal={setPolicyModal} setPage={setPage} />
                </>
            )}

            {page === 'home' && <ScrollIndicator />}
            {page === 'home' && (
                <StickyBuyBar 
                    visible={showSticky} 
                    onClick={() => handleBuyClick()} 
                />
            )}

            <LegalPage
                isOpen={!!policyModal}
                policyType={policyModal}
                onClose={() => setPolicyModal(null)}
            />

            <WhatsAppButton />
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
}

export default App;
